import { useMemo, useState } from "react";
import { ChoiceField, NumField } from "@/components/tool/CalcFields";
import { numField } from "@/lib/health/body-profile";
import { macrosFromCalories } from "@/lib/health/fitness";
import {
  BodyProfileHint,
  useBodyProfileSync,
} from "@/lib/health/use-body-profile";
import { SciStatGrid } from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

type Split = "balanced" | "lowcarb" | "highprotein";
const SPLITS: Record<Split, [number, number, number]> = {
  balanced: [30, 40, 30],
  lowcarb: [40, 20, 40],
  highprotein: [40, 40, 20],
};

export function MacroCalc() {
  const locale = getLocale();
  const [calories, setCalories] = useState("2000");
  const [split, setSplit] = useState<Split>("balanced");

  const profileSync = useBodyProfileSync({
    apply: (p) => {
      if (p.calorieTarget) setCalories(String(p.calorieTarget));
    },
    collect: () => ({ calorieTarget: numField(calories) }),
    deps: [calories],
  });

  const out = useMemo(() => {
    const c = Number(calories);
    if (!Number.isFinite(c) || c <= 0) return null;
    const [p, cb, f] = SPLITS[split];
    return macrosFromCalories(c, p, cb, f);
  }, [calories, split]);

  const g = (n: number) => `${Math.round(n).toLocaleString(locale)} g`;

  return (
    <div className="sci-card rounded-2xl space-y-4 p-6 sm:p-8">
      <NumField
        id="calories"
        label={m.macro_calories()}
        value={calories}
        onChange={setCalories}
        suffix="kcal"
      />
      <ChoiceField
        label={m.macro_split()}
        value={split}
        onChange={setSplit}
        options={[
          { value: "balanced", label: m.macro_balanced() },
          { value: "lowcarb", label: m.macro_lowcarb() },
          { value: "highprotein", label: m.macro_highprotein() },
        ]}
      />
      {out ? (
        <SciStatGrid
          items={[
            { label: m.macro_protein(), value: g(out.proteinG) },
            { label: m.macro_carbs(), value: g(out.carbsG) },
            { label: m.macro_fat(), value: g(out.fatG) },
          ]}
        />
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="macro-hint">
          {m.h_hint()}
        </p>
      )}
      {out ? (
        <span className="hidden" data-testid="macro-protein">
          {Math.round(out.proteinG)}
        </span>
      ) : null}
      <BodyProfileHint {...profileSync} />
    </div>
  );
}
